/**
 * Turning the one colour the server sends about a category into the colour a particular place on
 * the page needs.
 *
 * `Category_Color` (php) derives a hue from the category name and hands it over at one fixed
 * saturation and lightness, with no idea what it is about to be drawn on. Everything about
 * readability is decided here.
 *
 * The move that matters is targeting *relative luminance* rather than HSL lightness. Lightness is
 * not perceptual -- yellow at 45% and blue at 45% are nowhere near equally bright, so a palette
 * pinned to one lightness has contrast swinging by a factor of five across the hue circle, and the
 * hues that fail are not obvious by looking at the numbers. Pinning luminance instead makes the
 * contrast ratio identical for every hue: at the constants below, text lands on 4.57:1 against the
 * page and black on a fill lands on 14.6:1, whatever colour the category happens to be.
 *
 * Because a fill is always held at one luminance, the text on it does not need choosing either --
 * black always wins there by an order of magnitude, so there is no black-or-white decision to make.
 */

/** The page itself, #fafafa (css/main.scss) -- what text has to be legible against. */
const PAGE_LUMINANCE = 0.9562;

/**
 * Text sits just inside 4.5:1 on that background. The ceiling is 0.1736; below it only costs
 * contrast that is already sufficient, and darkens every category toward black.
 */
const TEXT_LUMINANCE = 0.17;

/**
 * A fill is a pale wash of the same hue -- the register the 2016 spreadsheet's own cells sat in
 * (#FFE599, #CFE2F3, #EAD1DC). Lower this toward 0.55 for more presence; black stays readable well
 * past that.
 */
const FILL_LUMINANCE = 0.68;

/** What a category with no colour from the server yet -- created locally, not saved -- gets. */
const NEUTRAL = '#737373';

function channelToLinear(channel) {
	return channel <= 0.03928
		? channel / 12.92
		: Math.pow((channel + 0.055) / 1.055, 2.4);
}

export function relativeLuminance([red, green, blue]) {
	return 0.2126 * channelToLinear(red)
		+ 0.7152 * channelToLinear(green)
		+ 0.0722 * channelToLinear(blue);
}

export function contrastRatio(a, b) {
	const [lighter, darker] = [relativeLuminance(a), relativeLuminance(b)].sort((x, y) => y - x);
	return (lighter + 0.05) / (darker + 0.05);
}

export function hexToRgb(hex) {
	let value = String(hex || '').trim().replace(/^#/, '');
	if(value.length === 3) {
		value = value.split('').map((c) => c + c).join('');
	}
	if(!/^[0-9a-fA-F]{6}$/.test(value)) {
		return null;
	}
	return [
		parseInt(value.slice(0, 2), 16) / 255,
		parseInt(value.slice(2, 4), 16) / 255,
		parseInt(value.slice(4, 6), 16) / 255,
	];
}

export function rgbToHex([red, green, blue]) {
	const channel = (c) => Math.round(Math.min(1, Math.max(0, c)) * 255)
		.toString(16)
		.padStart(2, '0')
		.toUpperCase();
	return `#${channel(red)}${channel(green)}${channel(blue)}`;
}

function rgbToHsl([red, green, blue]) {
	const max = Math.max(red, green, blue);
	const min = Math.min(red, green, blue);
	const lightness = (max + min) / 2;
	if(max === min) {
		return [0, 0, lightness]; // grey; hue is meaningless and must not be invented
	}
	const delta = max - min;
	const saturation = lightness > 0.5
		? delta / (2 - max - min)
		: delta / (max + min);
	let hue;
	if(max === red) {
		hue = ((green - blue) / delta) % 6;
	} else if(max === green) {
		hue = (blue - red) / delta + 2;
	} else {
		hue = (red - green) / delta + 4;
	}
	hue *= 60;
	if(hue < 0) {
		hue += 360;
	}
	return [hue, saturation, lightness];
}

function hslToRgb(hue, saturation, lightness) {
	const c = (1 - Math.abs(2 * lightness - 1)) * saturation;
	const x = c * (1 - Math.abs(((hue / 60) % 2) - 1));
	const m = lightness - c / 2;
	const sector = Math.floor(hue / 60) % 6;
	const table = [
		[c, x, 0], [x, c, 0], [0, c, x],
		[0, x, c], [x, 0, c], [c, 0, x],
	];
	return table[sector].map((channel) => channel + m);
}

/**
 * The same hue and saturation, moved to a given relative luminance.
 *
 * Luminance rises monotonically with lightness at a fixed hue and saturation, so a bisection lands
 * on it; there is no closed form, because the sRGB transfer curve is piecewise. Thirty halvings put
 * the answer far below one part in 255, which is all the precision a hex triplet can hold.
 */
export function atLuminance(hex, targetLuminance) {
	const rgb = hexToRgb(hex) || hexToRgb(NEUTRAL);
	const [hue, saturation] = rgbToHsl(rgb);

	let low = 0;
	let high = 1;
	for(let i = 0; i < 30; i++) {
		const middle = (low + high) / 2;
		if(relativeLuminance(hslToRgb(hue, saturation, middle)) < targetLuminance) {
			low = middle;
		} else {
			high = middle;
		}
	}
	return rgbToHex(hslToRgb(hue, saturation, (low + high) / 2));
}

/** For drawing the category *as* text, on the page background. */
export function asText(hex) {
	return atLuminance(hex, TEXT_LUMINANCE);
}

/** For drawing the category as a filled cell, with black text over it. */
export function asFill(hex) {
	return atLuminance(hex, FILL_LUMINANCE);
}

/** Text colour to use over `asFill()` -- constant, since the fill's luminance is. */
export const FILL_TEXT_COLOR = '#000000';

/** The text colour at an opacity, for the washes the category dropdown and pie chart draw. */
export function asTextRgba(hex, opacity = '1') {
	const [red, green, blue] = hexToRgb(asText(hex));
	const channel = (c) => Math.round(c * 255);
	return `rgba( ${[channel(red), channel(green), channel(blue)].join(',')} , ${opacity})`;
}

export default {
	asText,
	asFill,
	asTextRgba,
	atLuminance,
	contrastRatio,
	relativeLuminance,
	hexToRgb,
	rgbToHex,
	FILL_TEXT_COLOR,
	NEUTRAL,
	TEXT_LUMINANCE,
	FILL_LUMINANCE,
	PAGE_LUMINANCE,
};
